import { Close } from '@mui/icons-material';
import { Box, Button, Card, CardActions, CardContent, CardHeader, Grid, IconButton, Modal, TextField, Typography } from '@mui/material';
import { Dispatch, SetStateAction } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useToast } from '../../../../Hooks/useToast';
import { useUser } from '../../../../Hooks/useUser';
import { useUpdateClientPasswordMutation } from '../../../../Redux/api/clientApi';
import { useUpdateTalentPasswordMutation } from '../../../../Redux/api/talentApi';
import { ProfileType } from '../../../../Types/General';
import { KaziPassword } from '../../../../Shared Components/Kazi/KaziPassword';

interface EditPasswordModalForm {    
    currentPassword: string,
    newPassword: string,
}

interface EditPasswordModalProps {
    open: boolean;
    setOpen: Dispatch<SetStateAction<boolean>>;
    userId: string | undefined;
}

export const EditPasswordModal = ( { open, setOpen, userId }: EditPasswordModalProps ) => {
    /* ******************** Hooks ******************** */
    const { user, type }  = useUser();
    const { showToast } = useToast();
    const [
        updateTalentPassword, {
            data: talentPasswordResponse, 
            isLoading: talentPasswordLoading
        }
    ] = useUpdateTalentPasswordMutation();
    const [
        updateClientPassword, {
            data: clientPasswordResponse, 
            isLoading: clientPasswordLoading
        }
    ] = useUpdateClientPasswordMutation();
    const {
        control,
        formState: {isDirty},
        getValues,
        reset,
        handleSubmit
    } = useForm<EditPasswordModalForm>({
        defaultValues: {
            currentPassword: '',//accountInfo.first_name,
            newPassword: '',// accountInfo.last_name,
        }
    })
    /* ******************** Variables ******************** */
    /* ******************** Functions ******************** */
    const handleClose = () =>{
        reset();
        setOpen(false);
    }
    const handleSave = (data: EditPasswordModalForm) =>{
        if (type == ProfileType.Talent) {
            updateTalentPassword({
                locator: user?.locator!,
                password: data.newPassword
            })
            .unwrap()
            .then(res => {
                handleClose()
                showToast({
                    text: 'Password updated successfully',
                    variant: 'success'
                })
            })
            .catch(err => {
                console.log(err);
                showToast({
                    text: "Something went wrong",
                    variant: 'error'
                })
            })
        }
        if (type == ProfileType.Client) {
            updateClientPassword({
                locator: user?.locator!,
                password: data.newPassword
            })
            .unwrap()
            .then(res => {
                handleClose()
                showToast({
                    text: 'Password updated successfully',
                    variant: 'success'
                })
            })
            .catch(err => {
                console.log(err);
                showToast({
                    text: "Something went wrong",
                    variant: 'error'
                })
            })
        }
    }
    /* ******************** Effects ******************** */
    /* ******************** JSX ******************** */
    if (!userId) return;
    return (
        <Modal
            open={open}
            sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center"
            }}
        >
            <Card
                sx={{
                    width: "600px",
                    borderRadius: "12px",
                    maxHeight: "95%"
                }}
            >
                <CardHeader 
                    sx={{
                        paddingBottom: 1
                    }}                
                    action={
                        <IconButton onClick={handleClose}>
                            <Close color='primary'/>
                        </IconButton>
                    }
                />
                <CardContent
                    sx={{
                        padding: '0 24px'
                    }}
                >
                    <Grid container display={"flex"} flexDirection={"column"} gap={'20px'}>
                        <Typography variant='lora-title-large' color={'primary'} fontWeight={'bold'}>Change password</Typography> 
                        <Grid item xs display={"flex"} flexDirection={"column"}>
                            <Typography variant='body-small' color={'black.200'} marginBottom={1}>Current password</Typography> 
                                <Controller
                                    name='currentPassword'
                                    control={control}
                                    render={({field}) =>(
                                        <KaziPassword
                                            placeholder='Enter current password'
                                            {...field}
                                        />
                                    )}
                                />
                        </Grid>
                        <Grid item xs display={"flex"} flexDirection={"column"}>
                            <Typography variant='body-small' color={'black.200'} marginBottom={1}>New password</Typography> 
                                <Controller
                                    name='newPassword'
                                    control={control}
                                    render={({field}) =>(
                                        <KaziPassword
                                            placeholder='Enter new password'
                                            {...field}
                                        />
                                    )}
                                />
                        </Grid>
                    </Grid>
                </CardContent>
                <CardActions
                    sx={{
                        padding: '24px'
                    }}
                >
                    <Box flex={1} display={'flex'} flexDirection={'row'} justifyContent={'flex-end'} gap={1}>                            
                        <Button variant={'outlined'} onClick={handleClose}>Cancel</Button>                           
                        <Button 
                            variant={'contained'} 
                            disabled={!isDirty} 
                            onClick={handleSubmit(handleSave)}
                            sx={{
                                "&.Mui-disabled": {
                                    backgroundColor: 'black.50',
                                    border: 'none',
                                    color: 'primary.main'
                                }
                            }}
                        >
                            Save
                        </Button>
                    </Box>
                </CardActions>
            </Card> 
        </Modal>
    )
}